import { useRef } from 'react'
import { forEachObj } from 'remeda'

import type {
  ConditionsInput,
  CreateAutomationInput,
} from './automations-form.schema'
import { conditions } from './constants'

import type { ConditionSchemas } from './automations-base.schema'

type ConditionValue = Record<string, any>

type ServerConditionItem = {
  type: typeof conditions[number]
  value: ConditionValue
}

type ResultObject = Record<string, ConditionValue> | string

function useAutomationFormRefs() {
  const messageItemRef = useRef<HTMLDivElement | null>(null)

  return { messageItemRef }
}

function convertToObjectArray(obj: Partial<ConditionsInput>) {
  const output: Array<ServerConditionItem> = []
  const {
    loyaltyPointsActive,
    loyaltyPointsLifetime,
    loyaltyVisitsActive,
    sale,
    ...original
  } = obj

  const ranges = {
    loyaltyPointsActive,
    loyaltyPointsLifetime,
    loyaltyVisitsActive,
    sale,
  }

  if (original.assignedTo)
    output.push({
      type: 'assignedTo',
      value: {
        eq: {
          objectId: original.assignedTo,
          __type: 'Pointer',
          className: '_User',
        },
      },
    })

  if (original.soldBy)
    output.push({
      type: 'soldBy',
      value: {
        eq: {
          objectId: original.soldBy,
          __type: 'Pointer',
          className: '_User',
        },
      },
    })

  if (Number.isInteger(original.noMessages))
    output.push({
      type: 'noMessages',
      value: { gt: original.noMessages, delayUnit: 'days' },
    })

  if (original.notFirstSale)
    output.push({ type: 'notFirstSale', value: { eq: original.notFirstSale } })

  if (original.saleGroup?.length)
    output.push({
      type: 'saleGroup',
      value: {
        in: original.saleGroup.map((group) => ({
          objectId: group,
          __type: 'Pointer',
          className: 'Group',
        })),
      },
    })

  if (original.clientGroup?.length)
    output.push({
      type: 'clientGroup',
      value: {
        in: original.clientGroup.map((group) => ({
          objectId: group,
          __type: 'Pointer',
          className: 'Group',
        })),
      },
    })

  if (original.noSales)
    output.push({
      type: 'noSales',
      value: { gt: original.noSales, delayUnit: 'days' },
    })

  forEachObj.indexed(ranges, (val, key) => {
    if (val != null) {
      forEachObj.indexed(val, (v, k) => {
        if (v != null) {
          const item = { type: key, value: { [k]: v } }
          output.push(item)
        }
      })
    }
  })

  return output
}

function composeConditions(conditions: CreateAutomationInput['conditions']) {
  return convertToObjectArray(conditions)
}

function parseConditions(conditions: Array<ConditionSchemas>) {
  const resultObject: ResultObject = {}

  conditions.forEach((item) => {
    if (!resultObject[item.type]) {
      resultObject[item.type] = {}
    }

    if (item.type === 'assignedTo' || item.type === 'soldBy')
      resultObject[item.type] = item.value.eq.objectId
    else if (item.type === 'noMessages' || item.type === 'noSales')
      resultObject[item.type] = item.value.gt
    else if (item.type === 'notFirstSale')
      resultObject[item.type] = item.value.eq
    else if (item.type === 'clientGroup' || item.type === 'saleGroup')
      resultObject[item.type] = item.value.in.map(
        (i: { objectId: string }) => i.objectId
      )
    else Object.assign(resultObject[item.type], item.value)
  })

  return resultObject
}

export type { ResultObject, ConditionValue }
export { composeConditions, parseConditions, useAutomationFormRefs }
